import * as React from "react";
import { MessageBar, MessageBarType, TextField } from "@fluentui/react";
import { CustomPrimaryButton } from "../components/Design/Buttons";
import { useApplicationContext } from "../hooks/useApplicationContext";
import { useAuthenticationContext } from "../hooks/useAuthenticationContext";
import { IChangePasswordDTO } from "../models";
import { PasswordRequirementsText, validatePassword } from "../utils/PasswordPolicy";
import styles from "../styles/App.module.scss";

interface IChangePasswordProps
{

}

export const ChangePassword : React.FunctionComponent<IChangePasswordProps> = (props: React.PropsWithChildren<IChangePasswordProps>) => {

    const { Services } = useApplicationContext();
    const { user } = useAuthenticationContext();

    const [existingPassword, setExistingPassword] = React.useState<string>("");
    const [newPassword, setNewPassword] = React.useState<string>("");
    const [confirmNewPassword, setConfirmNewPassword] = React.useState<string>("");
    const [isWorking, setIsWorking] = React.useState<boolean>(false);
    const [showSuccessMessage, setShowSuccessMessage] = React.useState<boolean>(false);
    const [errorMessage, setErrorMessage] = React.useState<string | undefined>(undefined);

    const newPasswordError = newPassword ? validatePassword(newPassword, user?.email) : undefined;

    const validateNewPassword = () =>
    {
        if(!newPassword)
        {
            return "";
        }

        return newPasswordError ?? (newPassword === existingPassword ? "Den nye adgangskode skal være forskellig fra den nuværende." : "");
    }

    const validateConfirmNewPassword = () =>
    {
        if(!confirmNewPassword)
        {
            return "";
        }

        return newPassword === confirmNewPassword ? "" : "De nye adgangskoder er ikke ens.";
    }

    const canSubmit = !isWorking
        && !!existingPassword
        && !!newPassword
        && !!confirmNewPassword
        && !newPasswordError
        && newPassword !== existingPassword
        && newPassword === confirmNewPassword;

    const onSubmit = async (e: React.FormEvent) =>
    {
        e.preventDefault();

        setShowSuccessMessage(false);
        setErrorMessage(undefined);
        setIsWorking(true);

        const error = await Services?.Master.ChangePassword({
            existingPassword: existingPassword,
            newPassword: newPassword,
            confirmNewPassword: confirmNewPassword
        } as IChangePasswordDTO);

        setIsWorking(false);

        if(!error)
        {
            setShowSuccessMessage(true);
            setExistingPassword("");
            setNewPassword("");
            setConfirmNewPassword("");
        } else
        {
            setErrorMessage(error);
        }
    }

    return (
        <div className={styles.login}>
            <h3>Skift adgangskode</h3>
            <form onSubmit={onSubmit}>
                <TextField
                    required={true}
                    label="Nuværende adgangskode"
                    type="password"
                    autoComplete="current-password"
                    canRevealPassword
                    revealPasswordAriaLabel="Vis adgangskode"
                    value={existingPassword}
                    onChange={(ev, newValue) => { setExistingPassword(newValue + ""); }}
                />
                <TextField
                    required={true}
                    label="Ny adgangskode"
                    description={PasswordRequirementsText}
                    type="password"
                    autoComplete="new-password"
                    canRevealPassword
                    revealPasswordAriaLabel="Vis adgangskode"
                    value={newPassword}
                    validateOnLoad={false}
                    deferredValidationTime={500}
                    onGetErrorMessage={validateNewPassword}
                    onChange={(ev, newValue) => { setNewPassword(newValue + ""); }}
                />
                <TextField
                    required={true}
                    label="Bekræft ny adgangskode"
                    type="password"
                    autoComplete="new-password"
                    canRevealPassword
                    revealPasswordAriaLabel="Vis adgangskode"
                    value={confirmNewPassword}
                    onGetErrorMessage={validateConfirmNewPassword}
                    onChange={(ev, newValue) => { setConfirmNewPassword(newValue + ""); }}
                />
                <div className={styles.submitButtonContainer}>
                    <CustomPrimaryButton disabled={!canSubmit} text="Skift adgangskode" />
                </div>
                {showSuccessMessage &&
                    <MessageBar messageBarType={MessageBarType.success}>
                        Din adgangskode er nu ændret.
                    </MessageBar>
                }
                {errorMessage &&
                    <MessageBar messageBarType={MessageBarType.error}>
                        {errorMessage}
                    </MessageBar>
                }
            </form>
        </div>
    );
}

export default ChangePassword;
